import { OmniaApiInterface } from "./api/apiInterface"
import { OmniaApiChange } from "./api/types"
import { OmniaModuleId } from "./generated/enums"
import { omniaResolve } from "./promises"
import { IOmniaService, IOmniaSession, IOmniaSettings } from "./types"
import { getStackTrace } from "./utils/utils"

/** omnia-i18n-ignore-strings */
export abstract class OmniaService implements IOmniaService {

    protected abstract getRefreshPromise(full?: boolean, scope?: any): ng.IPromise<any>

    protected _readOnly = this.session.isReadOnly()

    protected _ready: boolean

    protected _stateVersion = 0
    protected _changeNumber = 0
    private _year = 0

    private _refresh: ng.IPromise<any>

    constructor(
        protected readonly session: IOmniaSession,
        private readonly _config: {
            name: string
            module: OmniaModuleId,
            dependencies: { refresh: () => ng.IPromise<any> }[]
        },
        protected readonly moduleId = _config?.module,
        protected readonly ohttp = session.http,
        protected readonly apiState = session.apiState,
        protected readonly apiInterface = new OmniaApiInterface(
            session.http,
            session.apiState,
            () => this.refresh(),
            {
                source: moduleId
            }
        )) {
    }

    get init(): boolean {
        return (this._stateVersion > 0)
    }

    get ready(): boolean {
        return this._ready
    }

    get readOnly() {
        return this._readOnly
    }

    get permissions() {
        return this.session.permissions
    }

    get stateVersion(): number {
        return this._stateVersion
    }

    get holdingId(): number {
        return this.session.holdingId
    }

    get holding() {
        return this.session.holding
    }

    get yearId(): number {
        return this.session.yearId
    }

    get cropYear() {
        return this.session.cropYear
    }

    protected shouldRefresh(full?: boolean) {
        if (full) return true
        if (this.stateVersion <= 0) return true
        if (this._changeNumber <= 0) return true
        return (this.session.getApiVersion() > this._changeNumber)
    }

    getSession(): IOmniaSession {
        return this.session
    }

    getSettings(namespace: string): IOmniaSettings {
        return this.session.getSettings(namespace)
    }

    refresh(force?: boolean) {
        return this.refreshInternal(force)
    }

    ensureInit() {

        if (this._refresh) {
            return this._refresh
        }

        if (this._stateVersion > 0) {
            return omniaResolve()
        }

        return this.refresh()


    }

    protected refreshInternal(full?: boolean, scope?: any): ng.IPromise<any> {

        if (this.session && this.session.reloading) {
            // When reloading the application after switching to another holding,
            // the location is changed back to /#/dashboard, which triggers a reinitialisation of the module
            // There is no point in doing any of that while reloading, since the instance will eventually go away,
            // so we are disabling any refresh if reloading
            return omniaResolve()
        }

        if (this._refresh) {
            this.logRefresh(full, scope, "refresh in progress")
            return this._refresh
        }

        full = full || (this._year != this.session.yearId)

        const deps = this._config.dependencies.reduce((p, c) => {
            return p ? p.then(() => c.refresh()) : c.refresh()
        }, undefined as ng.IPromise<any>)

        const local = full || (this._stateVersion <= 0) || (this._changeNumber <= 0) || this.shouldRefresh(full)
            ? () => this.getRefreshPromise(full, scope)
            : undefined

        const refresh = local
            ? deps
                ? deps.then(local)
                : local()
            : deps

        if (!refresh) {
            this.logRefresh(full, scope, "no changes")
            return omniaResolve()
        }

        this._ready = false

        const caller = this.session?.debugMode
            ? getStackTrace()
            : undefined

        return (this._refresh = refresh.then(_ => {

            this.logRefresh(full, scope, local ? undefined : "no changes", caller)

            this._refresh = undefined
            this._ready = true
            this._changeNumber = this.session.getApiVersion()
            this._year = this.session.yearId

            if (local) {
                this._stateVersion += 1
            }

        }))

    }

    private logRefresh(full: boolean, scope: any, skipReason?: string, caller?: string[]) {

        const { session } = this

        if (session?.debugMode) {

            const args = {
                full,
                skip: skipReason,
                scope,
                name: this._config?.name,
                caller: caller || getStackTrace(),
                thisChangeNumber: this._changeNumber,
                apiChangeNumber: this.session.getApiVersion()
            }

            const thisclass = <any>this.constructor?.name || "service"

            console.log(`%c${thisclass}.refresh`, (skipReason ? "color: #555" : "font-weight: bold"), args)

        }

    }

    public resolve<T>(value: T) {
        return omniaResolve(value)
    }

    protected get<T>(url: string): ng.IPromise<T> {
        return this.apiInterface.get<T>(url)
    }

    protected post(url: string, data?: any, change?: false | Omit<OmniaApiChange, "id" | "local">, refresh = true) {
        return this.apiInterface.post(url, data, change, refresh)
    }

    protected put(url: string, data?: any, change?: false | Omit<OmniaApiChange, "id" | "local">, refresh = true) {
        return this.apiInterface.put(url, data, change, refresh)
    }

    protected delete(url: string, data?: any, change?: false | Omit<OmniaApiChange, "id" | "local">, refresh = true) {
        return this.apiInterface.delete(url, data, change, refresh)
    }

    protected recordChange(change?: Omit<OmniaApiChange, "id" | "local">, refresh = true) {
        return this.apiInterface.recordChange(change, refresh)
    }

}
